import { useUncontrolled } from '@mantine/hooks';
import { ActionIcon, Flex, Image, Input, Paper, Text } from '@mantine/core';
import { Dropzone as MantineDropzone } from '@mantine/dropzone';
import { IconFileUpload, IconTrash, IconUpload, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

const toEntries = (value) => {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr.map((file) => ({
    file,
    preview: URL.createObjectURL(file),
    id: Math.random().toString(36).slice(2),
  }));
};

export const Dropzone = ({
  multiple = false,
  value,
  defaultValue,
  onChange,
  label = null,
  description,
  inputLabel,
  withUploadIcon = true,
  maxFiles = 5,
}) => {
  const [files, setFiles] = useUncontrolled({
    value: value !== undefined ? toEntries(value) : undefined,
    defaultValue: defaultValue !== undefined ? toEntries(defaultValue) : undefined,
    finalValue: [],
    onChange: (entries) =>
      onChange?.(multiple ? entries.map((e) => e.file) : entries[0]?.file ?? null),
  });

  const handleDrop = (acceptedFiles) => {
    const mapped = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).slice(2),
    }));

    setFiles(multiple ? [...files, ...mapped] : mapped.slice(0, 1));
  };

  const handleDelete = (id) => {
    setFiles(files.filter((f) => f.id !== id));
  };

  const renderPreview = (item) => {
    const isImage = item.file.type.startsWith('image/');

    return (
      <Paper key={item.id} p="xs" radius="md" withBorder style={{ position: 'relative', width: 120 }}>
        {isImage ? (
          <Image src={item.preview} alt={item.file.name} height={80} fit="contain" />
        ) : (
          <Flex align="center" justify="center" style={{ height: 80 }}>
            <IconFileUpload size={32} />
          </Flex>
        )}

        <Text size="xs" truncate>
          {item.file.name}
        </Text>

        <ActionIcon
          color="red"
          size="sm"
          radius="xl"
          style={{ position: 'absolute', top: 5, right: 5 }}
          onClick={() => handleDelete(item.id)}
        >
          <IconTrash size={14} />
        </ActionIcon>
      </Paper>
    );
  };

  const handleReject = (rejectedFiles) => {
    const groupedErrors = rejectedFiles.reduce((acc, file) => {
      file.errors.forEach((error) => {
        if (!acc[error.code]) {
          acc[error.code] = {
            title: error.message,
            fileNames: [],
          };
        }

        const fileName = (file.file.relativePath || file.file.path)
          .split('/')
          .pop();

        acc[error.code].fileNames.push(fileName);
      });

      return acc;
    }, {});

    Object.values(groupedErrors).forEach(({ title, fileNames }) => {
      notifications.show({
        title,
        message: fileNames.join(',\n'),
        color: 'red',
      });
    });
  };

  return (
    <>
      <Input.Label>{inputLabel}</Input.Label>
      <Flex direction="column" gap="md">
        <MantineDropzone
          maxSize={100 * 1024 ** 2} // 100MB in KB
          maxFiles={maxFiles}
          multiple={multiple}
          onDrop={(acceptedFiles) => {
            const remainingSlots = maxFiles - files.length;

            if (remainingSlots <= 0) {
              notifications.show({
                title: `Too many files, you can only upload up to ${maxFiles} files`,
                message: acceptedFiles.map((file) => file.name).join(',\n'),
                color: 'red',
              });
              return;
            }

            if (acceptedFiles.length > remainingSlots) {
              const allowed = acceptedFiles.slice(0, remainingSlots);
              const rejected = acceptedFiles.slice(remainingSlots);

              handleDrop(allowed);

              notifications.show({
                title: `Too many files, you can only upload up to ${maxFiles} files`,
                message: rejected.map((file) => file.name).join(',\n'),
                color: 'red',
              });

              return;
            }

            handleDrop(acceptedFiles);
          }}
          onReject={handleReject}
          radius="lg"
        >
          <Flex justify="center" align="center" direction="column" gap="md" mih={72} style={{ pointerEvents: 'none' }}>
            <MantineDropzone.Accept>
              {withUploadIcon && (
                <IconFileUpload size={52} color="var(--smartico-blue)" stroke={1.5} />
              )}
            </MantineDropzone.Accept>
            <MantineDropzone.Reject>
              <IconX size={52} color="var(--mantine-color-red-6)" stroke={1.5} />
            </MantineDropzone.Reject>
            <MantineDropzone.Idle>
              {withUploadIcon && (
                <IconUpload size={22} stroke={1.5} />
              )}
            </MantineDropzone.Idle>

            <Flex justify="center" align="center" direction="column">
              {label != false && (
                <Text fz="14" fw="600" inline>
                  {label || 'Drag files here or click to select'}
                </Text>
              )}
              <Text fz="12" fw="400" c="dimmed" inline mt={label != false ? 7 : 0}>
                {description || 'Attach any file type, max 5MB each'}
              </Text>
            </Flex>
          </Flex>
        </MantineDropzone>

        {files.length > 0 && (
          <Flex wrap="wrap" gap="sm">
            {files.map((item) => renderPreview(item))}
          </Flex>
        )}
      </Flex>
    </>
  );
};